import * as React from 'react';
import { graphql } from 'react-apollo';

import { branch, compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import contentfulGql from 'js/lib/contentfulGql';
import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';
import { getCmsLocaleString } from 'js/lib/language';

import type { BrowseEntityPageContentProps } from 'bundles/browse-common/types/faqs';
import type { PropsFromCaller as CareerPlansPropsFromCaller } from 'bundles/career-plans/components/CareerPlansPage';
import withDataFormattingAndProductLookup from 'bundles/career-plans/components/data/withDataFormattingAndProductLookup';
import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';

type PropsFromRouter = {
  router: InjectedRouter;
  isPreview: boolean;
  slug: string;
};

type GraphqlVariables = { preview: boolean; slug: string; locale: string };
type GraphqlData = { careerPlansPageContentCollection: { items: $TSFixMe[] } };
type PropsFromGraphql = {
  pageContent?: BrowseEntityPageContentProps;
};

const CareerPlansQuery = contentfulGql`#graphql
  query CareerPlansQuery($preview: Boolean!, $slug: String!, $locale: String!) {
    careerPlansPageContentCollection(preview: $preview, locale: $locale, where: { slug: $slug }, limit: 1) {
      items {
        slug
        name
        description
        ctaCalloutText
        socialProofStatistics: socialProofStatisticsCollection(limit: 10) {
          items {
            title
            subtitle
          }
        }
        socialProofStatisticsSource
        socialProofStatisticsSourceDescription
        careers: careersCollection(limit: 10) {
          items {
            name
            skillLevel
            description
            keySkills
            foundationalCourses: foundationalCoursesCollection(limit: 30) {
              items {
                ... on CareerPlansCourseOrS12N {
                  id
                  name
                  descriptionOverride
                  isCourse
                  isSpecialization
                  isProject
                  isMasterTrack
                  isProfessionalCertificate
                  partners
                }
              }
            }

            showcasedCourses: showcasedCoursesCollection(limit: 30) {
              items {
                ... on CareerPlansCourseOrS12N {
                  id
                  name
                  descriptionOverride
                  isCourse
                  isSpecialization
                  isProject
                  isMasterTrack
                  isProfessionalCertificate
                  partners
                }
              }
            }
          }
        }
        faqs: faqsCollection(limit: 20) {
          items {
            sectionName
            question
            answer
          }
        }
        testimonials: testimonialsCollection(limit: 10) {
          items {
            title
            subtitle
            description
            callToActionLink
            visual {
              url
              description
            }
          }
        }
        degrees
        metaTitle
        metaDescription
      }
    }
  }
`;

const withCareerPlansContentfulData = () => (BaseComponent: React.ComponentType<{}>) => {
  const componentName = BaseComponent.displayName || BaseComponent.name;

  class HOC extends React.Component<CareerPlansPropsFromCaller> {
    static displayName = `withCareerPlansContentfulData(${componentName})`;

    render() {
      // @ts-expect-error TS2559: Type '{ appSlug: string; faqTopic: string; isEntityPage?: boolean | undefined; hideSchemaMarkup?: boolean | undefined; }' has no properties in common with type 'IntrinsicAttributes'.
      return <BaseComponent {...this.props} />;
    }
  }

  hoistNonReactStatics(HOC, BaseComponent);

  return compose<CareerPlansPropsFromCaller, {}>(
    connectToRouter((router) => {
      const previewParam = router.location.query.preview;
      const isPreview = Number(previewParam) === 1;

      return {
        isPreview,
        router,
        slug: router.params.slug,
      };
    }),
    graphql<PropsFromRouter, GraphqlData, GraphqlVariables, PropsFromGraphql>(CareerPlansQuery, {
      options: ({ isPreview, slug }) => {
        return {
          variables: {
            preview: !!isPreview,
            slug,
            locale: getCmsLocaleString(),
          },
          context: getGrowthCmsGraphqlContext(isPreview),
        };
      },
      props: ({ data }) => ({
        pageContent: data?.careerPlansPageContentCollection?.items[0],
        loading: data?.loading,
      }),
    }),
    branch(({ pageContent }: PropsFromGraphql) => !!pageContent, compose(withDataFormattingAndProductLookup))
  )(HOC);
};

export default withCareerPlansContentfulData;
